import { array, fn } from '@ember/helper';
import { tracked } from '@glimmer/tracking';
import { service } from '@ember/service';
import Component from '@glimmer/component';
import { Header, Section, VStack } from 'tio-ui/components/layout';
import { Listbox } from 'tio-ui/components/collections';
import { Popover } from 'tio-ui/components/popover';
import { eq } from 'tio-ui/utilities';
import { t } from 'ember-intl';
import { PhCaretDown } from 'ember-phosphor-icons';
import ApplicationRow from 'tio-employee/components/tas/dashboard/application-row';
import ProgramInstanceCard from 'tio-employee/components/tas/dashboard/program-instance-card';
import BenefitHistorySummary from 'tio-common/components/tas/benefit-history-summary';
import type { IntlService } from 'ember-intl';
import type PartnerService from 'tio-employee/services/partner';
import type {
  RecentApplication,
  ProgramInstance,
} from 'tio-common/utils/tuition-assistance/application-states';
import type { BenefitHistoryModel } from 'tio-employee/routes/authenticated/tas/benefit-history';

interface UnifiedHistoryItem {
  type: 'application' | 'instance';
  updatedAt: string;
  application?: RecentApplication;
  instance?: ProgramInstance;
}

interface BenefitHistorySignature {
  Args: {
    model: BenefitHistoryModel;
  };
}

const getHistoryDate = (application: RecentApplication) => {
  return (
    application.tasProgramInstanceCreatedAtDate ||
    application.tasApplicationCreatedAtDate ||
    application.documentCreatedAt ||
    application.createdAt
  );
};

export default class TasBenefitHistory extends Component<BenefitHistorySignature> {
  @service declare intl: IntlService;
  @service declare partner: PartnerService;

  @tracked selectedYear = '';

  get selectedYearValue() {
    return this.selectedYear || this.args.model.currentYear.toString();
  }

  get yearOptions() {
    const options: string[] = ['all'];

    // Add years in descending order
    for (const year of this.args.model.availableYears) {
      options.push(year.toString());
    }

    return options;
  }

  get yearLabel() {
    if (this.selectedYearValue === 'all') {
      return this.intl.t('tas.benefit_history.all_years');
    }
    const year = parseInt(this.selectedYearValue, 10);
    return `${year}-${year + 1}`;
  }

  getYearOptionLabel = (option: string) => {
    if (option === 'all') {
      return this.intl.t('tas.benefit_history.all_years');
    }
    const year = parseInt(option, 10);
    return `${year}-${year + 1}`;
  };

  get filteredApplications() {
    if (this.selectedYearValue === 'all') {
      return this.args.model.applications;
    }

    const yearFilter = parseInt(this.selectedYearValue, 10);
    const filtered = this.args.model.applications.filter((app) => {
      const dateStr = getHistoryDate(app);
      if (!dateStr) {
        return false;
      }
      const year = new Date(dateStr).getFullYear();
      const matches = year === yearFilter || year === yearFilter + 1;

      // Academic year spans two calendar years (e.g., 2025-2026 includes 2025 and 2026)
      return matches;
    });
    return filtered;
  }

  get filteredProgramInstances() {
    if (this.selectedYearValue === 'all') {
      return this.args.model.programInstances;
    }

    const yearFilter = parseInt(this.selectedYearValue, 10);
    const filteredInstances = this.args.model.programInstances
      .map((instance) => {
        // Filter child applications by year
        const filteredChildren = instance.childApplications.filter((app) => {
          const dateStr = getHistoryDate(app);
          if (!dateStr) return false;
          const year = new Date(dateStr).getFullYear();
          return year === yearFilter || year === yearFilter + 1;
        });

        // Return instance with filtered children if any match
        if (filteredChildren.length > 0) {
          return {
            instanceApplication: instance.instanceApplication,
            childApplications: filteredChildren,
          };
        }
        return null;
      })
      .filter((instance): instance is NonNullable<typeof instance> => instance !== null);

    return filteredInstances;
  }

  get sortedHistoryItems() {
    const items: UnifiedHistoryItem[] = [];

    // Add flat applications
    for (const app of this.filteredApplications) {
      items.push({
        type: 'application',
        updatedAt: app.updatedAt || '',
        application: app,
      });
    }

    // Add Type 1 and Type 4.2/4.3 instances
    for (const instance of this.filteredProgramInstances) {
      items.push({
        type: 'instance',
        updatedAt: instance.instanceApplication.updatedAt || '',
        instance,
      });
    }

    // Sort by updated_at descending (most recent first)
    return items.sort((a, b) => new Date(b.updatedAt).getTime() - new Date(a.updatedAt).getTime());
  }

  get hasHistory() {
    return this.sortedHistoryItems.length > 0;
  }

  get externalSpendLabel() {
    return (
      this.partner.settingModules?.externalSpendDescriptor ||
      this.intl.t('tas.dashboard.annual_benefits.external')
    );
  }

  handleYearSelect = (closePopover: () => void, key: string) => {
    this.selectedYear = key;
    closePopover();
  };

  <template>
    <VStack>
      {{! Benefit History Section - placeholder for future content }}
      <Section>
        <:header>
          <Header>{{t "tas.benefit_history.title"}}</Header>
        </:header>
        <:body>
          <BenefitHistorySummary
            @limitsStatusPrevious={{@model.limitsStatusPrevious}}
            @limitsStatusCurrent={{@model.limitsStatusCurrent}}
            @limitsStatusNext={{@model.limitsStatusNext}}
            @externalSpendLabel={{this.externalSpendLabel}}
          />
        </:body>
      </Section>

      {{! Activity Section }}
      <Section>
        <:header>
          <div class="flex items-center justify-between">
            <Header>{{t "tas.benefit_history.activity_title"}}</Header>
            <Popover as |pop|>
              <button
                type="button"
                class="flex items-center gap-1.5 px-2 py-1.5 text-xs font-medium text-gray-500 border border-gray-300 rounded-lg hover:bg-gray-50"
                data-test-benefit-history-year-filter
                {{pop.trigger}}
                {{pop.anchor}}
              >
                {{t "tas.benefit_history.view"}}
                {{this.yearLabel}}
                <PhCaretDown @size={{12}} />
              </button>
              <pop.Content @size="sm" @class="overflow-hidden">
                <Listbox
                  @isKeyboardEventsEnabled={{true}}
                  @allowEmpty={{true}}
                  @selectedKeys={{array this.selectedYearValue}}
                  @onAction={{fn this.handleYearSelect pop.close}}
                  @intent="primary"
                  @selectionMode="single"
                  @class="w-full"
                  data-test-benefit-history-year-options
                  as |l|
                >
                  {{#each this.yearOptions as |option|}}
                    <l.Item class="[&>span]:text-sm" @key={{option}}>
                      {{this.getYearOptionLabel option}}
                    </l.Item>
                  {{/each}}
                </Listbox>
              </pop.Content>
            </Popover>
          </div>
        </:header>
        <:body>
          {{#if this.hasHistory}}
            <div class="flex flex-col gap-2">
              {{#each this.sortedHistoryItems as |item|}}
                {{#if (eq item.type "instance")}}
                  {{! Type 4.2/4.3 - Expandable Instance Card }}
                  {{#let item.instance as |instance|}}
                    {{#if instance}}
                      <ProgramInstanceCard @instance={{instance}} @defaultExpanded={{true}} />
                    {{/if}}
                  {{/let}}
                {{else}}
                  {{! Type 1/2/3 - Flat Application Row }}
                  {{#let item.application as |application|}}
                    {{#if application}}
                      <ApplicationRow @application={{application}} />
                    {{/if}}
                  {{/let}}
                {{/if}}
              {{/each}}
            </div>
          {{else}}
            <div class="text-center py-12 text-gray-500">
              {{#if (eq this.selectedYearValue "all")}}
                {{t "tas.benefit_history.no_activity"}}
              {{else}}
                {{t "tas.benefit_history.no_activity_for_year" year=this.selectedYearValue}}
              {{/if}}
            </div>
          {{/if}}
        </:body>
      </Section>
    </VStack>
  </template>
}
